/**
 * Dialog para editar regra de roteamento existente
 * 
 * Suporta condições compostas (AND/OR) e sequência de ações
 * usando RoutingActionBuilder.
 * Permite selecionar inboxes específicas ou aplicar a todas.
 * 
 * @version 2.1.0
 * @date 2025-12-21
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CompositeConditionBuilder, 
  CompositeConditionConfig,
  isCompositeFormat,
  legacyToComposite,
} from './CompositeConditionBuilder';
import { RoutingActionBuilder } from './RoutingActionBuilder';
import { RoutingRule, RoutingConditionType } from '@/pages/tenant/RoutingRules';
import { RoutingActionItem } from './types';
import { RoutingActionType } from '@/pages/tenant/RoutingRules';
import { InboxSelector } from './InboxSelector';
import { WebhookConditionConfig } from './WebhookConditionConfig';
import { RuleVisibilitySelector, RuleVisibility } from './RuleVisibilitySelector';
import { RoutingCategorySelect } from './categories/RoutingCategorySelect';
import { createLogger } from '@/lib/logger';
import { showToast } from '@/lib/toast';

const routingLogger = createLogger('EditRuleDialog');

// Interface para mapeamento de campos do webhook
interface FieldMapping {
  contact_phone: string;
  contact_name: string;
}

interface EditRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: RoutingRule;
  onSuccess: () => void;
}

export function EditRuleDialog({ open, onOpenChange, rule, onSuccess }: EditRuleDialogProps) {
  const { user, currentTenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState(true);
  
  // Validação de segurança: apenas criador pode editar regras manuais próprias
  const isManualExecution = rule.condition_type === 'manual_execution';
  const isOwnRule = !rule.created_by || rule.created_by === user?.id;
  
  // Campos do formulário
  const [name, setName] = useState(rule.name);
  const [description, setDescription] = useState(rule.description || '');
  const [conditionConfig, setConditionConfig] = useState<CompositeConditionConfig>(() => {
    // Verifica se já está no formato composto
    if (isCompositeFormat(rule.condition_value)) {
      return rule.condition_value as unknown as CompositeConditionConfig;
    }
    // Converte formato legado
    return legacyToComposite(rule.condition_type, rule.condition_value);
  });
  const [actions, setActions] = useState<RoutingActionItem[]>([]);
  const [stopOnMatch, setStopOnMatch] = useState(rule.stop_on_match);
  
  // Estados para seleção de inboxes
  const [allInboxes, setAllInboxes] = useState(!rule.inbox_ids || rule.inbox_ids.length === 0);
  const [selectedInboxIds, setSelectedInboxIds] = useState<string[]>(rule.inbox_ids || []);
  
  // Estados de visibilidade (apenas para regras manuais)
  const [visibility, setVisibility] = useState<RuleVisibility>(
    ((rule as any).visibility as RuleVisibility) || 'private'
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  // Categoria atual da regra (pode vir hidratada do RPC)
  const [categoryId, setCategoryId] = useState<string | null>(
    ((rule as any).category_id as string | null) ?? null
  );
  
  // Estado para mapeamento de campos do webhook
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>(() => {
    const fm = (rule as any).field_mapping;
    return fm ? { contact_phone: fm.contact_phone || 'telefone', contact_name: fm.contact_name || 'nome' } 
              : { contact_phone: 'telefone', contact_name: 'nome' };
  });
  
  // Verifica se alguma condição é do tipo webhook
  const hasWebhookCondition = conditionConfig.conditions.some(c => c.type === 'webhook');

  // Carregar ações e allowed_users da regra quando abrir
  useEffect(() => {
    if (open && rule.id) {
      loadRuleActions();
      // Carregar usuários permitidos para regras manuais
      if (rule.condition_type === 'manual_execution') {
        loadAllowedUsers();
      }
    }
  }, [open, rule.id]);

  // Atualiza valores quando regra muda
  useEffect(() => {
    setName(rule.name);
    setDescription(rule.description || '');
    setStopOnMatch(rule.stop_on_match);
    
    // Atualizar estados de inboxes
    setAllInboxes(!rule.inbox_ids || rule.inbox_ids.length === 0);
    setSelectedInboxIds(rule.inbox_ids || []);
    
    // Atualizar visibilidade
    setVisibility(((rule as any).visibility as RuleVisibility) || 'private');

    // Atualizar categoria
    setCategoryId(((rule as any).category_id as string | null) ?? null);
    
    // Atualizar field_mapping do webhook
    const fm = (rule as any).field_mapping;
    setFieldMapping(fm ? { contact_phone: fm.contact_phone || 'telefone', contact_name: fm.contact_name || 'nome' } 
                       : { contact_phone: 'telefone', contact_name: 'nome' });
    
    // Converter condição para formato composto
    if (isCompositeFormat(rule.condition_value)) {
      setConditionConfig(rule.condition_value as unknown as CompositeConditionConfig);
    } else {
      setConditionConfig(legacyToComposite(rule.condition_type, rule.condition_value));
    }
  }, [rule]);

  // Carrega ações da regra a partir do payload já hidratado pelo backend
  // (get_tenant_routing_rules_full retorna actions[] e allowed_user_ids[] embutidos)
  const loadRuleActions = async () => {
    setLoadingActions(true);
    try {
      const embedded = ((rule as any).actions || []) as Array<any>;

      if (embedded.length > 0) {
        const loadedActions: RoutingActionItem[] = embedded.map((item, index) => ({
          id: item.id,
          action_type: item.action_type as RoutingActionType,
          action_value: item.action_value as Record<string, unknown>,
          sort_order: item.sort_order ?? index,
          delay_seconds: item.delay_seconds || 0,
        }));
        setActions(loadedActions);
      } else {
        // Fallback: criar ação a partir dos campos legados
        setActions([{
          id: crypto.randomUUID(),
          action_type: rule.action_type as RoutingActionType,
          action_value: rule.action_value,
          sort_order: 0,
          delay_seconds: 0,
        }]);
      }
    } catch (error) {
      routingLogger.error('Erro ao carregar ações', error);
      setActions([{
        id: crypto.randomUUID(),
        action_type: rule.action_type as RoutingActionType,
        action_value: rule.action_value,
        sort_order: 0,
        delay_seconds: 0,
      }]);
    } finally {
      setLoadingActions(false);
    }
  };

  // Carrega usuários permitidos (já vem hidratado em allowed_user_ids[])
  const loadAllowedUsers = async () => {
    try {
      const ids = ((rule as any).allowed_user_ids || []) as string[];
      setSelectedUserIds(ids);
    } catch (error) {
      routingLogger.error('Erro ao carregar usuários permitidos', error);
    }
  };

  // Validar condição
  const validateCondition = (cond: CompositeConditionConfig['conditions'][0]): boolean => {
    switch (cond.type) {
      case 'keyword':
        const keywords = (cond.value.keywords as string[]) || [];
        return keywords.length > 0;
      case 'regex':
        return !!(cond.value.pattern as string);
      case 'time_range':
        return !!(cond.value.start && cond.value.end);
      case 'first_message':
        return true; // Sempre válido - nova conversa
      case 'manual_execution':
        return true; // Sempre válido - execução manual não requer campos adicionais
      case 'webhook':
        return true; // Sempre válido - webhook não requer campos adicionais na condição
      case 'contact_tag':
        const tags = (cond.value.tag_ids as string[]) || [];
        return tags.length > 0;
      default:
        return false;
    }
  };

  // Salvar alterações
  const handleSubmit = async () => {
    // Validação de segurança para regras manuais
    if (isManualExecution && !isOwnRule) {
      showToast.error('Sem permissão', 'Você não pode editar regras de outros usuários');
      return;
    }
    
    // Validações básicas
    if (!name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    // Validação de inboxes
    if (!allInboxes && selectedInboxIds.length === 0) {
      toast.error('Selecione pelo menos uma inbox ou marque "Todas as inboxes"');
      return;
    }

    // Validação de condições
    const invalidConditions = conditionConfig.conditions.filter(c => !validateCondition(c));
    if (invalidConditions.length > 0) {
      toast.error('Preencha todas as condições corretamente');
      return;
    }

    // Validação de ações
    if (actions.length === 0) {
      toast.error('Adicione pelo menos uma ação');
      return;
    }

    setLoading(true);

    try {
      const firstCondition = conditionConfig.conditions[0];
      const firstAction = actions[0];

      // Atualiza regra + actions + allowed_users atomicamente via RPC
      // (invalida trigger_cache internamente)
      const { error: ruleError } = await supabase.rpc('update_routing_rule', {
        p_rule_id: rule.id,
        p_payload: {
          name: name.trim(),
          description: description.trim() || null,
          condition_type: firstCondition.type,
          condition_value: conditionConfig as unknown as Record<string, unknown>,
          action_type: firstAction.action_type,
          action_value: firstAction.action_value as Record<string, unknown>,
          stop_on_match: stopOnMatch,
          inbox_ids: allInboxes ? null : selectedInboxIds,
          field_mapping: hasWebhookCondition ? (fieldMapping as unknown as Record<string, unknown>) : null,
          ...(isManualExecution ? { visibility } : {}),
          ...(isManualExecution ? { category_id: categoryId } : {}),
        } as any,
        p_actions: actions.map((action, index) => ({
          action_type: action.action_type,
          action_value: action.action_value,
          sort_order: index,
          delay_seconds: action.delay_seconds || 0,
        })) as any,
        p_allowed_user_ids: isManualExecution
          ? (visibility === 'shared' ? selectedUserIds : [])
          : null,
        p_tenant_hint: currentTenant?.id,
      });

      if (ruleError) throw ruleError;

      routingLogger.info('Regra atualizada e cache de triggers invalidado');

      toast.success('Regra atualizada com sucesso');
      onSuccess();
    } catch (error) {
      routingLogger.error('Erro ao atualizar regra', error);
      toast.error('Erro ao atualizar regra');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Editar Macro</DialogTitle>
          <DialogDescription>
            Atualize as configurações da macro.
            Combine múltiplas condições usando E/OU.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          <div className="space-y-6 py-4">
            {/* Nome e Descrição */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome da Macro *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Rotear vendas em horário comercial"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descrição opcional da regra..."
                  rows={2}
                />
              </div>
            </div>

            {/* Seleção de Inboxes */}
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="font-medium">Aplicar em (Inboxes)</h3>
              <p className="text-sm text-muted-foreground">
                Selecione em quais inboxes esta regra será aplicada.
              </p>
              <InboxSelector
                allInboxes={allInboxes}
                selectedInboxIds={selectedInboxIds}
                onAllInboxesChange={setAllInboxes}
                onSelectedIdsChange={setSelectedInboxIds}
                showValidation={false}
              />
            </div>

            {/* Condições Compostas */}
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="font-medium">Quando (Condições)</h3>
              <p className="text-sm text-muted-foreground">
                Configure uma ou mais condições. Use E/OU para combinar.
              </p>
              <CompositeConditionBuilder
                config={conditionConfig}
                onChange={setConditionConfig}
              />
            </div>

            {/* Configuração de Webhook (apenas se condição for webhook) */}
            {hasWebhookCondition && (
              <div className="space-y-4 p-4 border rounded-lg border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-950/20">
                <h3 className="font-medium text-rose-700 dark:text-rose-300">Configuração do Webhook</h3>
                <WebhookConditionConfig
                  webhookToken={(rule as any).webhook_token}
                  ruleId={rule.id}
                  fieldMapping={fieldMapping}
                  onFieldMappingChange={setFieldMapping}
                  onTokenRegenerated={(newToken) => {
                    // Atualiza token no objeto da regra
                    (rule as any).webhook_token = newToken;
                  }}
                  isCreating={false}
                />
              </div>
            )}

            {/* Visibilidade (apenas para regras manuais) */}
            {isManualExecution && (
              <RuleVisibilitySelector
                visibility={visibility}
                onVisibilityChange={setVisibility}
                selectedUserIds={selectedUserIds}
                onSelectedUserIdsChange={setSelectedUserIds}
              />
            )}

            {/* Categoria (apenas para regras manuais) */}
            {isManualExecution && (
              <div className="p-4 border rounded-lg">
                <RoutingCategorySelect value={categoryId} onChange={setCategoryId} />
              </div>
            )}


            {/* Sequência de Ações */}
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="font-medium">Fazer (Sequência de Ações)</h3>
              {loadingActions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : (
                <RoutingActionBuilder
                  items={actions}
                  onItemsChange={setActions}
                  selectedInboxIds={allInboxes ? [] : selectedInboxIds}
                />
              )}
            </div>

            {/* Opções */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="stop-on-match">Parar ao dar match</Label>
                <p className="text-sm text-muted-foreground">
                  Se ativado, não verifica as próximas regras quando esta der match
                </p>
              </div>
              <Switch
                id="stop-on-match"
                checked={stopOnMatch}
                onCheckedChange={setStopOnMatch}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 shrink-0 border-t mt-auto">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading || loadingActions}>
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
